import React, { useState, useEffect } from "react";
import { Form, Button, Card, Alert } from "react-bootstrap";
import { PersonCircle, Chat, Send } from "react-bootstrap-icons";
import axios from "axios";
import { timeAgo } from "../time/timeAgo";
import { BACKEND_URL } from "constants";
import { useNavigate } from "react-router-dom";

const getSafeTimeAgo = (timestamp) => {
  if (!timestamp) return "just now";

  const timestampDate = new Date(timestamp);
  const now = new Date();

  if (Number.isNaN(timestampDate.getTime())) return "just now";

  const diffMs = now.getTime() - timestampDate.getTime();

  if (diffMs <= 0) return "just now";
  if (diffMs < 60 * 1000) return "just now";

  const result = timeAgo(timestamp);

  if (!result || result.includes("-")) return "just now";

  return result;
};

const getTimeValue = (timestamp) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return null;

  return date.getTime();
};

const MessagePanel = ({
  receiver_user_id,
  receiver_photo_id = "",
  allowShowProfile = false,
  setShow = () => { },
}) => {
  const navigate = useNavigate();

  const [messageTimestamp, setMessageTimestamp] = useState({
    incoming_message: undefined,
    outgoing_message: undefined,
    filtered_timestamp: undefined,
  });

  const [responseMessage, setResponseMessage] = useState({
    msg: undefined,
    cals: undefined,
  });

  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!receiver_user_id) return;

    axios
      .post(BACKEND_URL + "/get-message-time", { receiver_user_id })
      .then((response) => {
        setMessageTimestamp(response.data);
      })
      .catch((e) => {
        console.error(e);
      });
  }, [receiver_user_id, responseMessage]);

  const incomingMessageTime = getTimeValue(messageTimestamp.incoming_message);
  const outgoingMessageTime = getTimeValue(messageTimestamp.outgoing_message);
  const filteredTime = getTimeValue(messageTimestamp.filtered_timestamp);

  const isLoadingMessageTime =
    messageTimestamp.incoming_message === undefined &&
    messageTimestamp.outgoing_message === undefined &&
    messageTimestamp.filtered_timestamp === undefined;

  const latestMessageTime = Math.max(
    incomingMessageTime || 0,
    outgoingMessageTime || 0
  );

  const hasExistingConversation = latestMessageTime > 0;

  const isFilterNewerThanConversation =
    filteredTime !== null &&
    hasExistingConversation &&
    filteredTime > latestMessageTime;

  const isWritable =
    !isLoadingMessageTime &&
    (!hasExistingConversation || isFilterNewerThanConversation);

  const shouldShowViewMessage =
    !isLoadingMessageTime && hasExistingConversation && !isWritable;

  const handleViewProfile = () => {
    navigate("/profile/" + receiver_user_id + "/" + receiver_photo_id);
  };

  const handleViewMessage = () => {
    navigate("/inbox/" + receiver_user_id);
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const payload = {
      receiver_user_id,
      content: message.trim(),
    };

    axios
      .post(BACKEND_URL + "/send-message", payload)
      .then(() => {
        setResponseMessage({
          msg: "Message is being sent",
          cals: "success",
        });

        setMessage("");
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 401) {
            setResponseMessage({
              msg: "Something went wrong. Please try again.",
              cals: "danger",
            });
          } else if (error.response.status === 400) {
            setResponseMessage({
              msg: "Bad request. Please check your input.",
              cals: "danger",
            });
          } else if (error.response.status === 403) {
            setResponseMessage({
              msg: "No photo on that user",
              cals: "danger",
            });
          } else {
            setResponseMessage({
              msg: "An error occurred. Please try again later.",
              cals: "danger",
            });
          }
        } else if (error.request) {
          setResponseMessage({
            msg: "No response from server. Please check your network connection.",
            cals: "danger",
          });
        } else {
          setResponseMessage({
            msg: "Error: " + error.message,
            cals: "danger",
          });
        }

        setShow(true);
        console.error(error);
      });
  };

  const latestMessageTimestamp =
    outgoingMessageTime && outgoingMessageTime >= (incomingMessageTime || 0)
      ? messageTimestamp.outgoing_message
      : messageTimestamp.incoming_message;

  const statusMessage =
    outgoingMessageTime && outgoingMessageTime >= (incomingMessageTime || 0)
      ? "You've texted this user " + getSafeTimeAgo(latestMessageTimestamp)
      : "This user has texted you " + getSafeTimeAgo(latestMessageTimestamp);

  return (
    <Card className="message-panel">
      <Card.Header className="message-panel-header">
        <h4 className="mb-0">Message</h4>
      </Card.Header>

      <Card.Body className="message-panel-card-body">
        {responseMessage.msg && (
          <Alert variant={responseMessage.cals} className="message-alert">
            {responseMessage.msg}
          </Alert>
        )}

        <Form className="message-panel-form">
          {isLoadingMessageTime ? (
            <Form.Group className="message-panel-body">
              <Form.Label>Loading...</Form.Label>
            </Form.Group>
          ) : shouldShowViewMessage ? (
            <Form.Group className="message-panel-body">
              <Form.Label>{statusMessage}</Form.Label>
            </Form.Group>
          ) : (
            <Form.Group controlId="messageInput" className="message-input-box">
              <Form.Label className="message-label">Your message</Form.Label>

              <Form.Control
                as="textarea"
                value={message}
                placeholder="Type your message here"
                className="message-textarea"
                onChange={(e) => setMessage(e.target.value)}
              />
            </Form.Group>
          )}

          <div className="message-panel-actions">
            {allowShowProfile ? (
              <Button
                onClick={handleViewProfile}
                variant="outline-secondary"
                className="message-action-button"
              >
                <PersonCircle className="me-1" />
                View Profile
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                className="message-action-button"
                onClick={() => setShow(false)}
              >
                Cancel
              </Button>
            )}

            {shouldShowViewMessage ? (
              <Button
                variant="outline-success"
                className="message-action-button"
                onClick={handleViewMessage}
              >
                <Chat className="me-1" />
                View Message
              </Button>
            ) : (
              <Button
                variant="outline-success"
                className="message-action-button"
                onClick={handleSendMessage}
                disabled={!isWritable || !message.trim()}
              >
                <Send className="me-1" />
                Send
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default MessagePanel;